import { ActionList, Table, type TableColumnDef, type TableGridRecordProps } from '@sqs/rosetta-compositions';
import { BackButton, Divider, Stack } from '@sqs/rosetta-elements';
import { Ellipses } from '@sqs/rosetta-icons';
import { Box, Button, Flex, Text } from '@sqs/rosetta-primitives';
import { useTheme } from '@sqs/rosetta-styled';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUserPermissionsForSchedulingInstance } from '../../apis/schedulingInstances';
import { fetchUserSchedulingInstances } from '../../apis/sqspUsers';
import { NAV_USERS } from '../../const/navigation';
import { PAGINATION_LIMIT_MIN } from '../../const/pagination';
import { useGetSqspUserRoleMap } from '../../hooks/useGetSqspUserRoleMap';
import { useOrgUserActions } from '../../hooks/useOrgUserActions';
import { usePlatformBreakpoint } from '../../hooks/usePlatformBreakpoint';
import { useSchedulingInstanceUserActions } from '../../hooks/useSchedulingInstanceUserActions';
import { useI18n } from '../../i18n';

import { SchedulingInstance } from '../../models/SchedulingInstance';
import { ORG_MANAGER, ORG_MEMBER, SqspUser } from '../../models/SqspUser';
import { selectEnterprise } from '../../stores/currentUser';
import { fetchInvitedOrgUser, filterSingleInvitedUser } from '../../stores/invitedOrgUsers';
import { fetchOrgUser, filterSingleUser, selectAllOrgUsers } from '../../stores/orgUsers';
import { RootState } from '../../stores/rootReducer';
import { fetchSchedulingInstances } from '../../stores/schedulingInstances';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { enterpriseHasSsoProvider } from '../../utils/enterpriseUtils';
import { isPendingInvitationStatus } from '../../utils/sqspUserUtils';
import { AcuityLoader } from '../common/AcuityLoader';
import { EmptyResults } from '../common/EmptyResults';
import { PermissionsMap } from '../common/PermissionsMap';
import { TableHeader } from '../common/Table/TableHeader';
import { TablePagination } from '../common/Table/TablePagination';
import { UserStatusChip } from '../common/UserStatusChip';
import { UserResendInvitationActionItem } from './UserResendInvitationActionItem';
import { UserImage } from './util';

export const UserView = () => {
  const { T, t } = useI18n();
  const { getUserRole } = useGetSqspUserRoleMap();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isMobile } = usePlatformBreakpoint();
  const { colors, borders, space } = useTheme();
  const { id } = params;
  const userId = parseInt(id || '', 10);
  const [userSchedulingInstances, setUserSchedulingInstances] = useState<SchedulingInstance[] | null>(null);
  const [loadingChangePermission, setLoadingChangePermission] = useState<boolean>(false);
  const isActiveUser = userId > 0;

  const {
    currentUser,
    enterpriseId,
    hasLoadedSchedulingInstances,
    hasSsoProvider,
    isLoading,
    orgUser,
    schedulingInstances
  } = useAppSelector((state: RootState) => {
    const enterprise = selectEnterprise(state.currentUser);
    const users = isActiveUser ? state.orgUsers : state.invitedOrgUsers;
    return {
      currentUser: state.currentUser.currentUser as SqspUser,
      enterpriseId: enterprise?.id,
      hasLoadedSchedulingInstances: !state.schedulingInstances.isLoading,
      hasSsoProvider: enterpriseHasSsoProvider(enterprise),
      isLoading: users.isLoading || state.currentUser.isLoading,
      orgUser: isActiveUser ? filterSingleUser(state, userId) : filterSingleInvitedUser(state, userId),
      schedulingInstances: selectAllOrgUsers(state.schedulingInstances)
    };
  });

  const fetchUser = useCallback(() => {
    if (enterpriseId) {
      if (isActiveUser) {
        dispatch(fetchOrgUser(enterpriseId, userId));
      } else {
        dispatch(fetchInvitedOrgUser(enterpriseId, userId));
      }
    }
  }, [dispatch, enterpriseId, isActiveUser, userId]);

  const refreshAfterUserAction = () => {
    if (enterpriseId) {
      fetchUser();
      dispatch(fetchSchedulingInstances(enterpriseId));
      setUserSchedulingInstances(null);
    }
  };

  const { renderOrgActionModals, showRemoveUserFromOrgModal, showChangeRoleModal } = useOrgUserActions({
    refresh: refreshAfterUserAction
  });
  const {
    renderInstanceUserActionModals,
    showAddToSchedulingInstanceModal,
    showChangeUserPermissionModal,
    showRemoveUserFromSchedulingInstanceModal
  } = useSchedulingInstanceUserActions({ refresh: refreshAfterUserAction });

  useEffect(() => {
    if (enterpriseId && userId && !orgUser) {
      fetchUser();
    }
  }, [enterpriseId, userId, orgUser, fetchUser]);

  useEffect(() => {
    if (enterpriseId && isActiveUser && !hasLoadedSchedulingInstances && orgUser) {
      dispatch(fetchSchedulingInstances(enterpriseId));
    }
  }, [enterpriseId, userId, hasLoadedSchedulingInstances, dispatch, isActiveUser, orgUser]);

  useEffect(() => {
    if (!userSchedulingInstances && enterpriseId && isActiveUser && orgUser) {
      fetchUserSchedulingInstances(enterpriseId, userId).then((res) => {
        setUserSchedulingInstances(res.data);
      });
    }
  }, [userSchedulingInstances, enterpriseId, userId, isActiveUser, orgUser]);

  const renderSchedulingInstances = useMemo(() => {
    if (isPendingInvitationStatus(orgUser?.status)) {
      return (
        <Box mx={isMobile ? 3 : 6}>
          <EmptyResults
            noImage
            title={t("This user hasn\u2019t accepted their invitation yet.", {}, { project: 'enterprise-dashboard' })}
            subtitle={t("Once a user accepts their invitation to the organization, you will be able to add them to a Scheduling Instance.",

            {}, {
              project: 'enterprise-dashboard' })
            } />

        </Box>);

    }

    if (!hasLoadedSchedulingInstances || userSchedulingInstances === null || schedulingInstances === null) {
      return (
        <Flex
          mx={isMobile ? 3 : 6}
          border={`${borders[1]} ${colors.gray[700]}`}
          height="20vh"
          alignItems="center"
          justifyContent="center">

          <AcuityLoader />
        </Flex>);

    }

    if (schedulingInstances.length === 0) {
      return (
        <EmptyResults
          title={t("You don't have any Scheduling Instances for your org yet.",

          {}, {
            project: 'enterprise-dashboard' })
          }
          subtitle={t("Create a Scheduling Instance from your Dashboard so that you can give users access to it.",

          {}, {
            project: 'enterprise-dashboard' })
          }
          actionButton={
          <Button.Primary type="button" size="medium" width={isMobile ? '100%' : ''} onClick={() => navigate('/')}>
              <T project="enterprise-dashboard">{"Go to Dashboard"}</T>
            </Button.Primary>
          } />);


    }

    if (schedulingInstances.length > 0 && userSchedulingInstances.length === 0) {
      const defaultUserString = t("This user", {}, { project: 'enterprise-dashboard' });
      return (
        <Box px={isMobile ? 3 : 6}>
          <EmptyResults
            title={t("{name} doesn't have access to any Scheduling Instances yet.",

            { name: orgUser?.name || defaultUserString }, {
              project: 'enterprise-dashboard' })
            }
            subtitle={t("Give this user access to a Scheduling Instance so they can start managing their appointments.",

            {}, {
              project: 'enterprise-dashboard' })
            }
            actionButton={
            <Button.Primary
              type="button"
              size="medium"
              width={isMobile ? '100%' : ''}
              onClick={() => showAddToSchedulingInstanceModal(userId)}>

                <T project="enterprise-dashboard">{"Add to a Scheduling Instance"}</T>
              </Button.Primary>
            } />

          {renderOrgActionModals()}
        </Box>);

    }

    const tableColumns: TableColumnDef<SchedulingInstance, any>[] = [
    {
      accessorKey: 'instanceName',
      header: ({ column }: any) =>
      <TableHeader label={t("Name", null, { project: 'enterprise-dashboard' })} column={column} />

    }];

    if (currentUser.role === ORG_MANAGER && orgUser?.role === ORG_MEMBER) {
      tableColumns.push(
        {
          accessorKey: 'permissions',
          enableSorting: false,
          header: ({ column }) =>
          <TableHeader
            label={t("Permissions", null, { project: 'enterprise-dashboard' })}
            column={column}
            tooltip={t("The permissions this user has within each Scheduling Instance.",

            {}, {
              project: 'enterprise-dashboard' })
            } />,


          cell: (cell) => <PermissionsMap permission={cell.getValue()} />
        },
        {
          accessorKey: 'id',
          enableSorting: false,
          header: ({ column }) =>
          <TableHeader label={t("Actions", null, { project: 'enterprise-dashboard' })} column={column} />,

          cell: (cell) => {
            const ariaLabel = t("Open scheduling instance actions", {}, { project: 'enterprise-dashboard' });
            const menuItems = ({ onRequestClose }: {onRequestClose: () => void;}) =>
            <Stack
              bg="white"
              direction="column"
              py={isMobile ? 0 : 1}
              divider={isMobile && <Divider as="div" role="presentation" />}>

                <ActionList.Item
                as="div"
                onClick={async () => {
                  if (enterpriseId) {
                    setLoadingChangePermission(true);
                    const resp = await getUserPermissionsForSchedulingInstance(
                      enterpriseId,
                      cell.row.original.id,
                      orgUser.id
                    );
                    setLoadingChangePermission(false);
                    showChangeUserPermissionModal(cell.row.original.id, orgUser.id, resp);
                    onRequestClose();
                  }
                }}>

                  <Flex justifyContent="space-between" aria-live="polite">
                    <T project="enterprise-dashboard">{"Change user permission"}</T>
                    {loadingChangePermission &&
                  <Box pl={2} pt={2}>
                        <AcuityLoader size="sm" />
                      </Box>
                  }
                  </Flex>
                </ActionList.Item>
                <ActionList.Item
                as="div"
                onClick={() => showRemoveUserFromSchedulingInstanceModal(cell.row.original.id, orgUser.id)}
                color={colors.red[400]}>

                  <T project="enterprise-dashboard">{"Remove Scheduling Instance access"}</T>
                </ActionList.Item>
              </Stack>;

            if (isMobile) {
              return (
                <ActionList.Sheet
                  renderHeader={() =>
                  <Text.Body textAlign="center" fontWeight={500} pt={3}>
                      {orgUser?.name}
                    </Text.Body>
                  }
                  aria-label={ariaLabel}>

                  {menuItems}
                </ActionList.Sheet>);

            }

            return (
              <ActionList.PopOver aria-label={ariaLabel} anchorPoint={{ x: 'center', y: 'top' }} position="bottom">
                {menuItems}
              </ActionList.PopOver>);

          }
        }
      );
    }

    return (
      <Box px={isMobile ? 3 : 4}>
        <Table
          columns={tableColumns}
          data={userSchedulingInstances}
          enableSort
          enablePagination
          initialState={{
            sorting: [
            {
              id: 'instanceName',
              desc: false
            }],

            pagination: {
              pageIndex: 0,
              pageSize: PAGINATION_LIMIT_MIN
            }
          }}>

          {!isMobile &&
          <>
              <Table.List />
            </>
          }
          {isMobile &&
          <Table.Grid
            sx={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              marginTop: 3
            }}
            renderRecord={({ record }: TableGridRecordProps<SchedulingInstance>) => {
              if (!record) {
                return null;
              }

              const cells = Object.fromEntries(record.getVisibleCells().map((cell) => [cell.column.id, cell]));
              return (
                <Table.Grid.Record>
                    <Flex>
                      <Flex flexDirection="column" p={3} gap={0.5} width="100%">
                        <Flex flexDirection="column" mb={1}>
                          <Flex justifyContent="space-between">
                            <Table.Grid.Record.Cell sx={{ fontWeight: 500 }} cell={cells.instanceName} />
                            <Table.Grid.Record.Cell cell={cells.id} />
                          </Flex>
                        </Flex>
                        {orgUser?.role !== ORG_MANAGER &&
                      <Flex gap={2} alignItems="center">
                            <Text.Caption fontSize={3}>
                              <T project="enterprise-dashboard">{"Permissions"}</T>
                            </Text.Caption>
                            <Table.Grid.Record.Cell cell={cells.permissions} />
                          </Flex>
                      }
                      </Flex>
                    </Flex>
                  </Table.Grid.Record>);

            }} />

          }
          <TablePagination />
        </Table>
      </Box>);

  }, [userSchedulingInstances, schedulingInstances, orgUser]);

  if (isLoading || !enterpriseId) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <AcuityLoader />
      </Flex>);

  }

  if (!userId || currentUser.id === orgUser?.id) {
    navigate(NAV_USERS);
  }

  if (!orgUser) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Text.Title>
          <T project="enterprise-dashboard">{"User Not Found"}</T>
        </Text.Title>
      </Flex>);

  }

  const actionMenu = () => {
    const ariaLabel = t("Open User Actions", {}, { project: 'enterprise-dashboard' });
    const menuItems = ({ onRequestClose }: {onRequestClose: () => void;}) => {
      if (!id || !enterpriseId) {
        return <></>;
      }
      return (
        <Stack direction="column" py={1} divider={isMobile && <Divider as="div" role="presentation" />}>
          {isPendingInvitationStatus(orgUser.status) ?
          <UserResendInvitationActionItem
            inviteCode={orgUser.inviteCode}
            enterpriseId={enterpriseId}
            onRequestClose={onRequestClose} /> :


          <>
              <ActionList.Item as="div" onClick={() => showChangeRoleModal(orgUser?.id)}>
                <T project="enterprise-dashboard">{"Change role"}</T>
              </ActionList.Item>
              {orgUser.role === ORG_MEMBER &&
            <ActionList.Item as="div" onClick={() => showAddToSchedulingInstanceModal(orgUser?.id)}>
                  <T project="enterprise-dashboard">{"Add user to a Scheduling Instance"}</T>
                </ActionList.Item>
            }
            </>
          }
          {!hasSsoProvider &&
          <ActionList.Item as="div" onClick={() => showRemoveUserFromOrgModal(orgUser)} color="red.400">
              {isPendingInvitationStatus(orgUser.status) ? <T
              project="enterprise-dashboard">{"Cancel invitation"}</T> : <T

              project="enterprise-dashboard">{"Remove user from org"}</T>
            }
            </ActionList.Item>
          }
        </Stack>);

    };

    if (isMobile) {
      return (
        <Flex ml={0} alignItems="center" justifyContent="center">
          <Flex justifyContent="flex-end" alignItems="center" height="100%">
            <ActionList.Sheet
              renderHeader={() =>
              <Text.Body textAlign="center" fontWeight={500} pt={3}>
                  {orgUser.name}
                </Text.Body>
              }
              aria-label={ariaLabel}
              renderTrigger={({ toggleActionListOpen }) =>
              <Button.Tertiary onClick={toggleActionListOpen} aria-label={ariaLabel}>
                  <Ellipses />
                </Button.Tertiary>
              }>

              {menuItems}
            </ActionList.Sheet>
          </Flex>
        </Flex>);

    }

    return (
      <Flex ml={2} alignItems="center">
        <Flex justifyContent="flex-end" alignItems="center" height="100%">
          <ActionList.PopOver
            aria-label={ariaLabel}
            renderTrigger={({ toggleActionListOpen }) =>
            <Button.Secondary size="large" onClick={toggleActionListOpen} aria-label={ariaLabel}>
                <Flex alignItems="center" gap={1}>
                  {<T project="enterprise-dashboard">{"Actions"}</T>}
                  <Ellipses />
                </Flex>
              </Button.Secondary>
            }
            anchorPoint={{ x: 'left' }}
            position="bottom">

            {menuItems}
          </ActionList.PopOver>
        </Flex>
      </Flex>);

  };

  return (
    <Box>
      <BackButton
        mt={isMobile ? 3 : 6}
        mb={isMobile ? 1 : 3}
        onClick={() => navigate('/users')}
        label={t("Users", {}, { project: 'enterprise-dashboard' })} />

      <Flex
        border={`${borders[1]} ${colors.gray['700']}`}
        padding={isMobile ? 4 : 7}
        flexDirection={isMobile ? 'column' : 'row'}>

        {!isMobile &&
        <Flex mr={2}>
            <UserImage name={orgUser.name} imageUrl={orgUser.image} />
          </Flex>
        }
        <Flex mx={isMobile ? 0 : 4} flexGrow="1">
          <Box width={'100%'}>
            {isMobile ?
            <Flex gap={1} justifyContent="space-between" alignItems="center">
                <Text.Body fontWeight={500} m={0}>
                  {orgUser.name}
                </Text.Body>
                {currentUser.id !== orgUser.id && actionMenu()}
              </Flex> :

            <Text.Title m={0}>{orgUser.name}</Text.Title>
            }
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="flex-start"
              alignItems={isMobile ? '' : 'center'}
              mt={space[2]}
              gap={isMobile ? 0 : 10}>

              <Flex alignItems="center" gap={2}>
                <Text.Body my={isMobile ? 0 : 1} color={colors.gray[400]}>
                  <T project="enterprise-dashboard">{"Email"}</T>
                </Text.Body>
                <Text.Body my={isMobile ? 0 : 1} fontWeight={isMobile ? '' : 500}>
                  {orgUser.email}
                </Text.Body>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <Text.Body my={isMobile ? 0 : 1} color={colors.gray[400]}>
                  <T project="enterprise-dashboard">{"Role"}</T>
                </Text.Body>
                <Text.Body my={isMobile ? 0 : 1} fontWeight={isMobile ? '' : 500}>
                  {getUserRole(orgUser.role)}
                </Text.Body>
              </Flex>

              <Flex alignItems="center" gap={2}>
                <Text.Body my={isMobile ? 0 : 1} color={colors.gray[400]}>
                  <T project="enterprise-dashboard">{"Status"}</T>
                </Text.Body>
                <Box>
                  <UserStatusChip status={orgUser.status} />
                </Box>
              </Flex>
            </Stack>
          </Box>
        </Flex>
        {!isMobile && currentUser.id !== orgUser.id && actionMenu()}
      </Flex>
      <Text.Subtitle>
        <T project="enterprise-dashboard">{"Scheduling Instances"}</T>
      </Text.Subtitle>
      <Box mx={!isMobile ? -6 : -3} mb={4} position="relative">
        {renderSchedulingInstances}
      </Box>
      {renderOrgActionModals()}
      {renderInstanceUserActionModals()}
    </Box>);

};